import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityTrendCard } from '@/features/memorization/activity-trend-card';
import { DailyFocusCard } from '@/features/memorization/daily-focus-card';
import { ProgressSection } from '@/features/memorization/progress-section';
import { QuranProgressMap } from '@/features/memorization/quran-progress-map';
import { SubmissionList } from '@/features/memorization/submission-list';
import { TargetList } from '@/features/memorization/target-list';
import { useIsPersonalSpace } from '@/features/personal-space/hooks';
import { useMyStudentProfile } from '@/features/students/hooks';
import { useHasCapability } from '@/lib/capabilities';

/**
 * "Hafalan Saya" - the memorizer's own home for their targets.
 *
 * docs/05_INFORMATION_ARCHITECTURE.md lists Memorization as an
 * authenticated navigation entry; until now it had no route. Targets were
 * reachable only through `/students/:id`, the shared *management*
 * workspace - so someone who set a target from the dashboard had to open
 * the roster and find their own row to see it again. That detour is the
 * whole reason this page exists.
 *
 * It composes existing surfaces rather than introducing a parallel one:
 * the same `TargetList`, `ProgressSection` and `QuranProgressMap` the
 * shared workspace renders, scoped to the caller's own Student profile.
 * No new endpoint, no new aggregate, nothing computed here that the
 * management view would report differently.
 */
export function HafalanPage(): JSX.Element {
  const canManageStudents = useHasCapability('students:manage');
  const {
    data: profile,
    isLoading,
    isError,
  } = useMyStudentProfile(!canManageStudents);
  const isPersonalSpace = useIsPersonalSpace();

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Hafalan Saya</h1>
        <p className="text-sm text-muted-foreground">
          {isPersonalSpace
            ? 'Target yang Anda tentukan sendiri, dan sejauh mana Anda sudah menghafalnya.'
            : 'Target hafalan Anda dan perkembangannya di pondok ini.'}
        </p>
      </div>

      {/* A broad-access caller has no Student profile of their own to show
          here (one role per user per organization), so this page cannot be
          about them. The navigation never offers it to them - this only
          catches a typed URL. */}
      {canManageStudents && (
        <Alert>
          <AlertDescription>
            Halaman ini menampilkan hafalan Anda sendiri. Untuk melihat hafalan santri,{' '}
            <Link to="/students" className="font-medium text-primary hover:underline">
              buka daftar santri
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}

      {!canManageStudents && isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {!canManageStudents && isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat hafalan Anda.</AlertDescription>
        </Alert>
      )}

      {/* Resolved-but-empty is a valid state, not an error: an account with
          no Student profile in this organization (a Parent-only caller, for
          example) simply has no hafalan of its own here. */}
      {!canManageStudents && !isLoading && !isError && (profile === null || profile === undefined) && (
        <Alert>
          <AlertDescription>
            Anda belum terdaftar sebagai santri di ruang ini, jadi belum ada hafalan yang bisa
            ditampilkan.
          </AlertDescription>
        </Alert>
      )}

      {profile !== null && profile !== undefined && (
        <>
          <DailyFocusCard studentId={profile.id} />

          <TargetList
            studentId={profile.id}
            mode={isPersonalSpace ? 'personal' : 'assigned'}
            title="Target Hafalan"
          />

          {/* Without this a solo learner could set a target and then had
              nowhere to record setoran against it: "Catat Setoran" lived
              only on /students/:id, and the personal space deliberately
              hides the roster that led there. The loop has to close
              somewhere the learner can actually reach. */}
          <SubmissionList studentId={profile.id} />

          <ProgressSection
            studentId={profile.id}
            mode={isPersonalSpace ? 'personal' : 'assigned'}
          />

          <ActivityTrendCard studentId={profile.id} />

          <QuranProgressMap studentId={profile.id} />
        </>
      )}
    </div>
  );
}
